import { updateSentrySchoolCode, updateSentryUsername } from '../../services/sentryAngularJsHandler';

/**
 * HelloUserController is a class that manages the user interaction flow for a specific task.
 * It initializes the controller, handles various button clicks, and manages the task flow.
 */
class HelloUserController {
    /**
     * Constructor for HelloUserController.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $location - AngularJS $location service.
     * @param {Object} $routeParams - AngularJS $routeParams service.
     * @param {Object} UserInfoService - Service to get user information.
     * @param {Object} TaskFlowService - Service to manage task flow.
     * @param {Object} TaskInfoService - Service to get task information.
     * @param {Object} $bridges - Service to access various bridge functionalities.
     * @param {Object} Candidate - Candidate model.
     * @param {Object} $translate - AngularJS $translate service.
     * @param {Object} $rootScope - AngularJS $rootScope service.
     * @param {Object} PWAService - Service for PWA-specific operations including managed device configuration.
     * @param {Object} $uibModal - Angular UI Bootstrap modal service.
     */
    constructor($scope, $location, $routeParams, UserInfoService, TaskFlowService, TaskInfoService, $bridges, Candidate, $translate, $rootScope, PWAService, $uibModal) {
        // Ensure that all necessary redirects for task flow are registered
        TaskFlowService.initRedirects($scope, $location);

        this._logIdentifier = 'HelloUserController';

        // Controller meta setup
        const $logger = $bridges.logger(),
              $auth   = $bridges.authentication(),
              $eventLog = $bridges.eventLog();

        // Controller initialization
        const candidateUUID = $auth.getUserUUID(),
              candidate     = UserInfoService.getUserInfo(candidateUUID);
        const taskUUID      = candidate.getTaskUUID();
        const taskInfo      = TaskInfoService.getTaskInfo();
        const candidateTask = taskInfo.getTaskDataByUUID(taskUUID);

        // Private scope variables
        $scope.userInfo = candidate;
        $scope.taskTitle = taskInfo.getSubjectOrTitle();

        $scope.step = !candidate.isIsPreRegistered() ? "capture-details" : "confirm-identification";

        // Bind methods to $scope
        $scope.onNotMeButtonClick = this.onNotMeButtonClick.bind(this);
        $scope.onConfirmedIdentityClick = this.onConfirmedIdentityClick.bind(this);
        $scope.onQuitButtonClick = this.onQuitButtonClick.bind(this);
        $scope.onResumeSessionClick = this.onResumeSessionClick.bind(this);
        $scope.onStartOverClick = this.onStartOverClick.bind(this);
        $scope.onContinueAsStoredCandidate = this.onContinueAsStoredCandidate.bind(this);
        $scope.onRegisterDetailsClick = this.onRegisterDetailsClick.bind(this);
        $scope.onSubmitEmailClick = this.onSubmitEmailClick.bind(this);
        $scope.getTranslate = this.getTranslate.bind(this);
        $scope.onEditSchoolCodeClick = this.onEditSchoolCodeClick.bind(this);

        // School code model (on controller scope so it survives ng-switch child scopes)
        $scope.schoolCodeModel = { value: '' };
        $scope.schoolCodeLocked = false;

        // Store necessary services in the instance
        this.$scope = $scope;
        this.$location = $location;
        this.$rootScope = $rootScope;
        this.TaskFlowService = TaskFlowService;
        this.candidate = candidate;
        this.taskInfo = taskInfo;
        this.$logger = $logger;
        this.$bridges = $bridges;
        this.$translate = $translate;
        this.candidateUUID = candidateUUID;
        this.UserInfoService = UserInfoService;
        this.PWAService = PWAService;
        this.$uibModal = $uibModal;

        // Pre-populate school code from managed configuration if on capture-details step
        if ($scope.step === 'capture-details') {
            this.initProvisionedSchoolCode();
        }

        // Done
        $logger.logExit(this, 'initialisation');
    }

    /**
     * Checks if there is existing data and handles the flow accordingly.
     */
    checkExistingData() {
        const { $rootScope, TaskFlowService } = this;

        if (this.taskInfo.getPackageType() === "examiner") {
            this.openInstructions();
            return;
        }
        
        if (this.taskInfo.getPackageType() === "specimen") {
            this.openEmailCapture();
            return;
        }

        $rootScope.rp = TaskFlowService.checkAndInitResponse(this.candidate, false);
        $rootScope.rp.then(() => {
            if (TaskFlowService.hasExistingDataLastResult()) {
                this.openSessionResume();
            } else {
                this.openInstructions();
            }
        });
    }

    openSessionResume() {
        // Populate previous candidate identity for display on resume screen
        const identity = this.TaskFlowService.getCandidateIdentity();

        if (identity) {
            const sanitize = (str) => {
                if (!str) return '';
                const div = document.createElement('div');
                div.appendChild(document.createTextNode(str));
                return div.innerHTML;
            };
            const nameParts = [sanitize(identity.firstName), sanitize(identity.lastName)].filter(Boolean);
            this.$scope.previousCandidateName = nameParts.join(' ') || undefined;
            this.$scope.previousCandidateEmail = sanitize(identity.email) || undefined;
        }
        this.$scope.isSpecimen = this.taskInfo.getPackageType() === 'specimen';

        // Compare current candidate with stored identity to detect a different person
        this.$scope.isDifferentCandidate = this._isDifferentCandidate(identity);

        this.$scope.step = 'session-resume';
    }

    /**
     * Checks whether the current candidate differs from the stored identity.
     * If the stored identity has no usable name or email, we can't tell — returns false.
     * @param {Object} storedIdentity - The identity from the existing response data.
     * @returns {boolean} true if the identities appear to belong to different people.
     * @private
     */
    _isDifferentCandidate(storedIdentity) {
        if (!storedIdentity) return false;

        const normalize = (str) => (str || '').trim().toLowerCase();
        const storedFirst = normalize(storedIdentity.firstName);
        const storedLast = normalize(storedIdentity.lastName);
        const storedEmail = normalize(storedIdentity.email);

        // If stored identity has no usable details, we can't compare
        if (!storedFirst && !storedLast && !storedEmail) return false;

        // Compare names if both sides have them
        const currentFirst = normalize(this.candidate.getFirstName());
        const currentLast = normalize(this.candidate.getLastName());
        if ((currentFirst || currentLast) && (storedFirst || storedLast)) {
            return currentFirst !== storedFirst || currentLast !== storedLast;
        }

        // Fall back to email comparison for specimen flows
        const currentEmail = normalize(this.candidate.getTargetEmail ? this.candidate.getTargetEmail() : '');
        if (currentEmail && storedEmail) {
            return currentEmail !== storedEmail;
        }

        return false;
    }

    /**
     * Opens the email capture screen for specimen assessments.
     */
    openEmailCapture() {
        this.$scope.step = 'capture-email';
    }

    /**
     * Handles the email submission and proceeds to the next step.
     * @param {Object} emailForm - The form containing the email.
     */
    onSubmitEmailClick(emailForm) {
        const { $logger, $scope, candidate } = this;
        $logger.logEnter(this, 'onSubmitEmailClick');

        // Show error box if the form is not valid
        if (!emailForm.$valid) {
            $scope.showEmailErrors = true;
            $logger.logExit(this, 'onSubmitEmailClick');
            return;
        }
        $scope.showEmailErrors = false;

        // Store the email address in the candidate object
        candidate.setTargetEmail(emailForm.email);

        // Continue with the normal flow
        const { $rootScope, TaskFlowService } = this;
        $rootScope.rp = TaskFlowService.checkAndInitResponse(this.candidate, false);
        $rootScope.rp.then(() => {
            if (TaskFlowService.hasExistingDataLastResult()) {
                this.openSessionResume();
            } else {
                this.openInstructions();
            }
        });

        $logger.logExit(this, 'onSubmitEmailClick');
    }

    /**
     * Opens the instructions page for the candidate.
     */
    openInstructions() {        
        console.log('loading path: ', "/instructions/" + this.candidateUUID);
        this.$location.path("/instructions/" + this.candidateUUID);
    }

    /**
     * Handles the "Not Me" button click event.
     */
    onNotMeButtonClick() {
        const { $logger, $scope } = this;
        $logger.logEnter(this, 'onNotMeButtonClick');
        $scope.step = "notme";
        $logger.info(this, "STEP: ", $scope.step);
        $logger.logExit(this, 'onNotMeButtonClick');
    }

    /**
     * Handles the "Confirmed Identity" button click event.
     */
    onConfirmedIdentityClick() {
        const { $logger } = this;
        $logger.logEnter(this, 'onConfirmedIdentityClick');
        this.checkExistingData();
        $logger.logExit(this, 'onConfirmedIdentityClick');
    }

    /**
     * Handles the "Quit" button click event.
     * @param {boolean} restart - Whether to restart the application.
     */
    onQuitButtonClick(restart) {
        const { $logger, $bridges } = this;
        $logger.logEnter(this, 'onQuitButtonClick');
        $bridges.runtime().quit(restart);
        $logger.logExit(this, 'onQuitButtonClick');
    }

    /**
     * Handles the "Resume Session" button click event.
     */
    onResumeSessionClick() {
        const { $logger, candidate } = this;
        console.log('onResumeSessionClick');
        $logger.logEnter(this, 'onResumeSessionClick');

        // Restore candidate identity from stored response data so personal
        // details (firstName, lastName, email) survive page reloads.
        const identity = this.TaskFlowService.getCandidateIdentity();
        if (identity) {
            if (identity.firstName !== undefined) candidate.setFirstName(identity.firstName);
            if (identity.lastName !== undefined) candidate.setLastName(identity.lastName);
            if (identity.email !== undefined && candidate.setTargetEmail) candidate.setTargetEmail(identity.email);
            this.UserInfoService.setUserInfo(candidate);
        }

        this.openInstructions();
        $logger.logExit(this, 'onResumeSessionClick');
    }

    /**
     * Handles the "Continue as <stored candidate>" button click.
     * Adopts the stored candidate's identity and proceeds to instructions.
     */
    onContinueAsStoredCandidate() {
        const { $logger, candidate } = this;
        $logger.logEnter(this, 'onContinueAsStoredCandidate');

        const identity = this.TaskFlowService.getCandidateIdentity();
        if (identity) {
            if (identity.firstName !== undefined) candidate.setFirstName(identity.firstName);
            if (identity.lastName !== undefined) candidate.setLastName(identity.lastName);
            if (identity.email !== undefined && candidate.setTargetEmail) candidate.setTargetEmail(identity.email);
            this.UserInfoService.setUserInfo(candidate);
        }

        this.openInstructions();
        $logger.logExit(this, 'onContinueAsStoredCandidate');
    }

    /**
     * Handles the "Start Over" button click event.
     * Shows a confirmation modal, then backs up existing response data and proceeds to instructions.
     */
    onStartOverClick() {
        const { $logger, $scope } = this;
        $logger.logEnter(this, 'onStartOverClick');

        const modalInstance = this.$uibModal.open({
            templateUrl: '/views/ib/modules/hellouser/modal/startOverWarningModal.html',
            windowClass: 'start-over-warning-modal',
            backdrop: 'static',
            keyboard: false
        });

        modalInstance.result.then(() => {
            this._executeStartOver();
        }, () => {
            // User cancelled - no action needed
        });

        $logger.logExit(this, 'onStartOverClick');
    }

    /**
     * Executes the start-over flow after user confirmation.
     * Backs up existing response data and proceeds to instructions.
     * @private
     */
    _executeStartOver() {
        const { $logger, $scope, $bridges, TaskFlowService } = this;

        $scope.startOverInProgress = true;
        $scope.startOverError = false;

        $bridges.data().backupAndClearResponseData()
            .then(() => {
                $logger.info(this, 'Response data backed up and cleared successfully');
                TaskFlowService.resetForStartOver();
                $scope.startOverInProgress = false;
                // Proceed to instructions — same destination as "Continue"
                this.openInstructions();
                $scope.$applyAsync();
            })
            .catch((error) => {
                $logger.error(this, 'Failed to backup response data:', error);
                $scope.startOverInProgress = false;
                $scope.startOverError = true;
                $scope.$applyAsync();
            });
    }

    /**
     * Handles the "Register Details" button click event.
     * @param {Object} registerForm - The form containing registration details.
     */
    onRegisterDetailsClick(registerForm) {
        const { $logger, $scope, TaskFlowService, candidate } = this;
        $logger.logEnter(this, 'onRegisterDetailsClick');

        // Show error box if the form is not valid
        if (!registerForm.$valid) {
            $scope.showErrors = true;
            $logger.logExit(this, 'onRegisterDetailsClick');
            return;
        }
        $scope.showErrors = false;

        candidate.setFirstName(registerForm.regFirstName);
        candidate.setLastName(registerForm.regLastName);
        candidate.setSchoolCode(this.$scope.schoolCodeModel.value);
        updateSentrySchoolCode(this.$scope.schoolCodeModel.value);

        // Update Sentry username with real name for specimen packages
        if (this.taskInfo.getPackageType() === 'specimen') {
            const fullName = [registerForm.regFirstName, registerForm.regLastName].filter(Boolean).join(' ') || 'Anon';
            const tasks = this.taskInfo.getTasks();
            const assessmentName = (tasks && tasks.length > 0 && tasks[0].title) || this.taskInfo.getSubjectOrTitle() || 'Unknown';
            updateSentryUsername(fullName, assessmentName);
        }

        this.UserInfoService.setUserInfo(candidate);

        this.checkExistingData();

        $logger.logExit(this, 'onRegisterDetailsClick');
    }

    initProvisionedSchoolCode() {
        this.PWAService.getProvisionedSchoolCode().then(code => {
            if (code) {
                this.$scope.schoolCodeModel.value = code;
                this.$scope.schoolCodeLocked = true;
                this.$scope.$applyAsync();
            }
        }).catch(err => {
            console.warn('HelloUserController: Failed to get provisioned school code:', err);
        });
    }

    onEditSchoolCodeClick() {
        const modalInstance = this.$uibModal.open({
            templateUrl: '/views/ib/modules/hellouser/modal/schoolCodeWarningModal.html',
            windowClass: 'school-code-warning-modal',
            backdrop: 'static',
            keyboard: false
        });

        modalInstance.result.then(() => {
            this.$scope.schoolCodeLocked = false;
            // Defer focus until digest removes ng-readonly from the input
            this.$scope.$applyAsync(() => {
                const el = document.getElementById('regSchoolCode');
                if (el) el.focus();
            });
        }, () => {
            // User chose "Keep current" - no action needed
        });
    }

    /**
     * Gets the translated text for the given key and parameters.
     * @param {string} i - The translation key.
     * @param {Object} b - The parameters for the translation.
     * @returns {string} The translated text.
     */
    getTranslate(i, b) {
        const { $translate } = this;
        return $translate.instant(i, b);
    }
}

export default HelloUserController;